import _ from 'lodash';
import p5 from 'p5';
import {loadAssets} from '../util/p5_util.js';
import {qw} from '../util.js';

class Card {
	constructor(data={}) {
        this.p = data.p;
        this.ctx = data.ctx;
		this.x = data.x||this.p.width/2;
		this.y = data.y||this.p.height/2;
		this.shadow = data.shadow||1;
		this.calculateSize();
	}
	calculateSize() {
		if (this.p.width>this.p.height) {
			let aspect = this.ctx.assets.cardBack.width/this.ctx.assets.cardBack.height;
			this.h = Math.min(this.p.height*0.5, 400);
			this.w = this.h*aspect;
		} else {
			let aspect = this.ctx.assets.cardBack.height/this.ctx.assets.cardBack.width;
			this.w = Math.min(this.p.width*0.5, 200);
			this.h = this.w*aspect;
		}
	}
	moveTo(targetX, targetY, speed=0.005) {
		this.x+=(targetX-this.x)*(speed*this.p.deltaTime);
		this.y+=(targetY-this.y)*(speed*this.p.deltaTime);
	}
	moveBy(amountX, amountY, speed=0.005) {
		this.moveTo(this.x+amountX, this.y+amountY, speed);
	}
	draw() {
		//this.calculateSize(); ??
		this.p.push();
		if (this.shadow>0) {
			this.p.drawingContext.shadowOffsetX = 10;
			this.p.drawingContext.shadowOffsetY = 10;
			this.p.drawingContext.shadowBlur = 20;
			this.p.drawingContext.shadowColor = this.p.color(0, 0, 0, this.shadow*255);
		}
		this.p.image(
			this.ctx.assets.cardBack,
			this.x-this.w/2,
			this.y-this.h/2,
			this.w,
			this.h,
		);
		this.p.pop();
	}
}

class Button {
	constructor(data={}) {
        this.p = data.p;
        this.ctx = data.ctx;
		this.x = data.hasOwnProperty('x') ? data.x : this.p.width/2;
		this.y = data.hasOwnProperty('y') ? data.y : this.p.height/2;
		this.text = data.text||'';
		this.textSize = data.textSize||12;
		this.bgColor = data.bgColor||this.p.color(0, 255, 0, 255);
        this.hoverColor = data.hoverColor||this.p.color(0, 240, 50, 255);
		this.textColor = data.textColor||this.p.color(255, 255, 255, 255);
		this.click = data.click||null;
		this.shadow = data.hasOwnProperty('shadow') ? data.shadow : 1;
		this.enabled = data.hasOwnProperty('enabled') ? data.enabled : true;
		this.visible = data.hasOwnProperty('visible') ? data.visible : true;
		this.opacity = data.hasOwnProperty('opacity') ? data.opacity : 1;
		this.touched = false;
		this.calculateSize();
		this.ctx.state.clickables.push(this);
	}
	mouseInBounds() {
		return this.p.mouseX > this.x - this.w / 2 &&
			this.p.mouseX < this.x + this.w / 2 &&
			this.p.mouseY > this.y - this.h / 2 &&
			this.p.mouseY < this.y + this.h / 2;
	}
	onClick() {
		if (this.enabled&&!this.ctx.timeout&&this.mouseInBounds()) {
			this.click?.();
			this.ctx.timeout = 1000;
		}
	}
	calculateSize() {
		// Calculate width and height - do it each frame because the text can be changed
		let bbox = this.ctx.assets.font.textBounds(this.text, 0, 0, this.textSize);
		// Clamp the max button size
		while (bbox.w>this.p.width*0.5||bbox.h>this.p.height*0.05) {
			this.textSize-=2;
			bbox = this.ctx.assets.font.textBounds(this.text, 0, 0, this.textSize);
		}
		this.w = bbox.w+50;
		this.h = bbox.h+50;
	}
	draw() {		
		if (!this.visible)
            return;
		this.calculateSize();
		// BUTTON BACKGROUND
		this.p.push();
		// If color is array make a gradient, if not make it a single color
        let selectedColor = this.mouseInBounds() ? this.hoverColor : this.bgColor;
		if (Array.isArray(selectedColor)) {
			let gradient = this.p.drawingContext.createLinearGradient(
				this.p.width/2,
                this.y-this.h/2,
                this.p.width/2,
                this.y+this.h/2,
            );
			for (var i = 0; i<selectedColor.length; i++) {
				let c = selectedColor[i];
				c.setAlpha(this.opacity*255);
				gradient.addColorStop(i/selectedColor.length, c);
			}
			this.p.drawingContext.fillStyle = gradient;
		} else {
			let c = selectedColor;
			c.setAlpha(this.opacity * 255);
			this.p.fill(c);
		}
		this.p.noStroke();
		this.p.rect(
			this.x-this.w/2,
			this.y-this.h/2,
			this.w,
			this.h,
			20,
		);
		this.p.pop();
		// BUTTON TEXT
		this.p.push();
		this.p.textAlign(this.p.CENTER, this.p.CENTER);
		this.p.textSize(this.textSize);
		this.textColor.setAlpha(this.opacity*255);
		this.p.fill(this.textColor);
        // this.p.textFont(this.ctx.assets.font);
		this.p.text(this.text, this.x, this.y);
		this.p.pop();
	}
}

const PAGE_STATE = _.keyBy(qw`LOADING ENTERING SHUFFLING DISTRIBUTE_CARDS ERROR`);

const promisify = (p, method, arg)=>new Promise(
    (resolve, reject)=>p[method](arg, resolve, reject));

const pLoadImage = (p, img)=>promisify(p, 'loadImage', img);

const pLoadFont = (p, font)=>promisify(p, 'loadFont', font);

export const run = (element, opt={})=>{
    let {onDeck, startLoading} = opt;
    let ctx = {
        cardImg: null,
        assets: {
            cardBack: null,
            font: null,
        },
        vw: 0,
        vh: 0,
        elapsedTime: 0,
        state: {clickables: []},
        timeout: 0,
        pageState: PAGE_STATE.LOADING,
    };
    let setupState = p=>{
        switch (ctx.pageState) {
            case PAGE_STATE.ENTERING: setupEnter(p); break;
            case PAGE_STATE.SHUFFLING: setupShuffle(p); break;
            case PAGE_STATE.DISTRIBUTE_CARDS: setupDistribute(p); break;
        }
    };
    let destroyState = ()=>{
        ctx.state = {clickables: []};
        ctx.elapsedTime = 0;
    };
    let loadAssets = async p=>{
        let stopLoading = startLoading();
        let [cardBack, font] = await Promise.all([
            pLoadImage(p, 'https://ik.imagekit.io/nn3phmxu4z/simona_cherry/static/tarotmail-assets/tarot-back.png'),
            pLoadFont(p, 'https://ik.imagekit.io/nn3phmxu4z/simona_cherry/static/tarotmail-assets/font-arimo.ttf'),
        ]);
        ctx.assets.cardBack = cardBack;
        ctx.assets.font = font;
        ctx.pageState = PAGE_STATE.ENTERING;
        stopLoading();
        setupEnter(p);
    };
    let drawLoading = p=>{
        // p.textAlign(p.CENTER, p.CENTER);
		// p.textSize(p.width*0.05);
		// p.fill(p.color(255, 255, 255, 255));
		// p.text('טוען...', p.width/2, p.height/2);
    };
    let setupEnter = p=>{
        ctx.state.btnBegin = new Button({
            p,
            ctx,
            text: 'לחצו כאן להתחלת ערבוב הקלפים',
            textSize: p.width*0.05,
            bgColor: [p.color(108, 207, 99), p.color(61, 173, 91)],
            hoverColor: [p.color(109, 237, 115), p.color(59, 196, 139)],
            textColor: p.color(222, 255, 244),
            click: ()=>{
                destroyState();
                ctx.pageState = PAGE_STATE.SHUFFLING;
                setupShuffle(p);
            },
        });
        ctx.state.cards = [];
        let numCards = 6;
        for (var i = 0; i<numCards; i++) {
            ctx.state.cards.push(new Card({
                p,
                ctx,
                y: -i * 150 - 100,
                shadow: 1 / numCards,
            }));
        }
    };
    const drawEnter = p=>{
        ctx.state.cards.forEach(card=>{
            card.moveTo(p.width/2, p.height/2);
            card.draw();
        });
        ctx.state.btnBegin.opacity = p.lerp(0, 1, ctx.elapsedTime / 1000);
        ctx.state.btnBegin.draw();
    };
    let setupShuffle = p=>{
        ctx.state.shuffling = true;
        ctx.state.shuffleDeck = inputDeck=>{
            const doTheShuffle = array=>{
                let currentIndex = array.length, randomIndex;
                while (currentIndex!=0) {
                    randomIndex = Math.floor(Math.random() * currentIndex);
                    currentIndex--;
                    [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
                }
                return array;
            }    
            let timer = 0, deck;
            if (inputDeck) {
                deck = inputDeck;
            } else {
                console.warn('No deck was passed to shuffleDeck(inputDeck), building full deck... Make sure this is the desired behaviour.');
                deck = [];
                for (var i = 1; i <= 78; i++)
                    deck.push(i);
                doTheShuffle(deck);
            }
            const run = ()=>{
                doTheShuffle(deck);
                timer = setTimeout(run, 1000);
            };
            timer = setTimeout(run, 1000);
            const stop = ()=>{
                if (timer) {
                    clearTimeout(timer);
                    timer = 0;
                }
                return deck;
            };
            return stop;
        };
        ctx.state.shuffleStopHandle = ctx.state.shuffleDeck();
        ctx.state.deck = null;
        ctx.state.cardSpeed = 0.005;
        ctx.state.cards = [];
        for (var i = 0; i < 2; i++) {
            let card = new Card({p, ctx});
            card.away = true;
            card.shadow = 0.5;
            card.lerpPos = 0;
            card.lerpStart = p.width/2;
            if (i==0) {
                card.lerpEnd = p.width/2-card.w/2-50;
            } else if (i==1) {
                card.lerpEnd = p.width/2+card.w/2+50;
            }
            ctx.state.cards.push(card);
        }
        ctx.state.btnPause = new Button({
            p,
            ctx,
            text: 'הפסקת ערבוב',
            textSize: ctx.vw*5,
            bgColor: [p.color(108, 207, 99), p.color(61, 173, 91)],
            hoverColor: [p.color(109, 237, 115), p.color(59, 196, 139)],
            textColor: p.color(222, 255, 244),
            click: ()=>{
                if (!ctx.state.hasOwnProperty('btnEndFade')) {
                    ctx.state.btnEndFade = 0;
                    ctx.state.btnEnd.enabled = true;
                    ctx.state.btnEnd.visible = true;
                }
                ctx.state.shuffling = !ctx.state.shuffling;
                if (ctx.state.shuffling) {
                    ctx.state.shuffleStopHandle = ctx.state.shuffleDeck(ctx.state.deck);
                    ctx.state.btnPause.text = 'הפסקת ערבוב';
                    ctx.state.btnEnd.visible = false;
                    ctx.state.btnEnd.enabled = false;
                    delete ctx.state.btnEndFade;
                } else {
                    if (ctx.state.shuffleStopHandle) {
                        ctx.state.deck = ctx.state.shuffleStopHandle();
                        console.log('The Deck', ctx.state.deck);
                    }
                    ctx.state.btnPause.text = 'אני רוצה לערבב עוד קצת';
                }
            }
        });
        ctx.state.btnEnd = new Button({
            p,
            ctx,
            enabled: false,
            opacity: 0,
            visible: false,
            y: p.height * 0.75,
            text: 'סיום ערבוב וקבלת המסרים',
            textSize: p.width * 0.03,
            bgColor: [p.color(235, 185, 106), p.color(219, 92, 53)],
            hoverColor: [p.color(250, 232, 142), p.color(245, 148, 69)],
            textColor: p.color(222, 255, 244),
            click: ()=>{
                if (!ctx.state.deck) {
                    console.error('BIG ASS ERROR! state.deck doesn\'t exist.');
                    return; 
                }
                let theFinalDeck = ctx.state.deck.slice(0, 7);
                console.log('The Final Deck', theFinalDeck);
                // Send async API call
                onDeck(theFinalDeck);
                destroyState();
                ctx.pageState = PAGE_STATE.DISTRIBUTE_CARDS;
                setupDistribute(p);
            },
        });
    };
    const drawShuffle = p=>{
        ctx.state.cards.forEach(card=>{
            if (ctx.state.shuffling) {
                if (card.away&&card.lerpPos>=1) {
                    card.away = false;
                    card.lerpPos = 1;
                } else if (!card.away&&card.lerpPos<=0) {
                    card.away = true;
                    card.lerpPos = 0;
                }
                card.x = p.lerp(card.lerpStart, card.lerpEnd, card.lerpPos);
                let delta = p.deltaTime*ctx.state.cardSpeed;
                if (!card.away)
                    delta*=-1;
                card.lerpPos+=delta;
            } else {
                card.away = false;
                card.x = p.lerp(card.lerpStart, card.lerpEnd, card.lerpPos);
                card.lerpPos-=p.deltaTime*ctx.state.cardSpeed;
                if (card.lerpPos<0)
                    card.lerpPos = 0;
            }
            card.draw();
        });
	    ctx.state.btnPause.draw();
        if (ctx.state.hasOwnProperty('btnEndFade')) {
            ctx.state.btnEnd.opacity = p.lerp(0, 1, ctx.state.btnEndFade);
            ctx.state.btnEndFade+=p.deltaTime*0.005;
        }
        ctx.state.btnEnd.draw();
    };
    const setupDistribute = p=>{
	    ctx.state.exitingCard = new Card({p, ctx});
	    ctx.state.deckDisplay = [];
	    ctx.state.deckEndPosition = [];
	    ctx.state.xPadding = ctx.vw*5;
	    ctx.state.yPadding = ctx.vh*5;
	    for (var i = 0; i<7; i++) {
            // Insert card
            let card = new Card({p, ctx});
            card.y = -card.h/2-50;
            ctx.state.deckDisplay.push(card);
            // Determine end position
            let xDiff = p.width-ctx.state.xPadding-card.w;
            let yDiff = p.height-ctx.state.yPadding-card.h;
            card.endPositionX = ctx.state.xPadding+card.w/2+xDiff/7*i;
            card.endPositionY = ctx.state.yPadding+card.h/2+yDiff/7*i;
        }
        ctx.state.textSize = Math.min(ctx.vw, ctx.vh)*10;
        ctx.state.textStr = 'המסר הראשון בדרך למייל שלך';
    };
    const drawDistribute = p=>{
        if (ctx.state.exitingCard.y>-ctx.state.exitingCard.h/2-50) {
            ctx.state.exitingCard.moveTo(p.width/2, -ctx.state.exitingCard.h/2-100, 0.003);
            ctx.state.exitingCard.draw();
        } else {
            for (var i = 0; i<ctx.state.deckDisplay.length; i++) {
                let card = ctx.state.deckDisplay[i];
                if (i==0||ctx.state.deckDisplay[i - 1].y>Math.min(ctx.vh*5, ctx.state.yPadding)) {
                    card.moveTo(card.endPositionX, card.endPositionY);
                    card.draw();
                    continue;
                }
            }
        }
        /*
        push();
        noFill();
        strokeWeight(4);
        stroke(255, 0, 0);
        const card = state.exitingCard;
        const xPadding = vw * 5;
        const yPadding = vh * 5;
        const xDiff = width - xPadding * 2;
        const yDiff = height - yPadding * 2;
        rect(xPadding, yPadding, xDiff, yDiff);
        pop();
        */
        if (ctx.state.deckDisplay[6].y>Math.min(ctx.state.yPadding, 20)) {
            // Display text on screen
            if (!ctx.state.hasOwnProperty('textFade'))
                ctx.state.textFade = 0;
            p.push();
            p.textAlign(p.CENTER);
            p.textSize(ctx.state.textSize);
            p.textWrap(p.WORD);
            p.fill(255, 255, 255, ctx.state.textFade * 255);
            p.strokeWeight(2);
            p.stroke(0, 0, 0, ctx.state.textFade * 255);
            p.drawingContext.shadowOffsetX = 0;
            p.drawingContext.shadowOffsetY = 0;
            p.drawingContext.shadowBlur = 50;
            p.drawingContext.shadowColor = p.color(0, 0, 0, ctx.state.textFade * 255);
            // p.textFont(ctx.assets.font);
            p.text(ctx.state.textStr, ctx.vw*15, ctx.vh*30, ctx.vw*70);
            p.pop();
            ctx.state.textFade+=p.deltaTime*0.003;
        }
    };
    let draw = p=>{
        p.clear();
        ctx.elapsedTime+=p.deltaTime;
        if (ctx.timeout>0)
            ctx.timeout-=p.deltaTime;
        else if (ctx.timeout<0)
            ctx.timeout = 0;
        p.push();
        switch (ctx.pageState) {
            case PAGE_STATE.LOADING: drawLoading(p); break;
            case PAGE_STATE.ENTERING: drawEnter(p); break;
            case PAGE_STATE.SHUFFLING: drawShuffle(p); break;
            case PAGE_STATE.DISTRIBUTE_CARDS: drawDistribute(p); break;
        }
        p.pop();
    };
    let setup = p=>{
        let canvas = p.createCanvas(p.windowWidth, p.windowHeight);
        canvas.parent(element);
        ctx.vw = p.width*0.01;
        ctx.vh = p.height*0.01;
        loadAssets(p);
    };
    let mouseClicked = p=>ctx.state.clickables.forEach(c=>c.onClick?.(p));
    let windowResized = p=>{
        p.resizeCanvas(p.windowWidth, p.windowHeight);
        ctx.vw = p.width*0.01;
        ctx.vh = p.height*0.01;
        destroyState();
        setupState(p);
    };
    let p5Instance = new p5(p=>{
        p.setup = ()=>setup(p);
        p.mouseClicked = ()=>mouseClicked(p);
        p.windowResized = ()=>windowResized(p);
        p.draw = ()=>draw(p);
    });
    return {stop: ()=>p5Instance.remove()};
};
